import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { directory, orgchart } from '@cwos/api-client';
import {
  Button,
  ConsequenceNote,
  Pill,
  SkeletonSet,
  SuccessEmpty,
  useTabListKeys,
} from '@cwos/ui-admin';
import { mayListWorkers, usePermissions } from '../permissions.js';
import { authorityGate, gateOpen, gateSentence } from '../authorityGate.js';
import { writeRefusalMessage } from '../ceilingRefusal.js';
import { listPhase } from '../listState.js';
import { pickerState } from '../pickerState.js';
import { personDirectory, type PersonDirectory } from '../personDirectory.js';
import { localDayString } from '../../lib/timeFormat.js';
import { PersonChip } from '../PersonChip.js';
import {
  borrowedSummary,
  coverageWindowSentence,
  formatDate,
  managerCountSentence,
  managerLine,
  reportCountSentence,
} from './orgChartLogic.js';
import { LoadFailure } from '../LoadFailure.js';
import { AuthorityHistory } from './AuthorityHistory.js';
import { ChartEditing } from './ChartEditing.js';
import { DelegationCoverage } from './DelegationCoverage.js';

/**
 * The org chart, for one person at a time.
 *
 * ---------------------------------------------------------------------------
 * THERE IS NO TREE WIDGET HERE, AND THAT IS THE DESIGN
 * ---------------------------------------------------------------------------
 * The obvious rendering of an org chart is a tree: boxes, one line up from each
 * box to its parent. That shape CANNOT REPRESENT THIS DATA. A person in this
 * organisation has an organizational manager at org scope and a program
 * supervisor at program scope simultaneously; both are real, both route work,
 * and neither is subordinate to the other. A tree has one edge upward, so
 * drawing one means choosing which of somebody's two managers to erase — and
 * the choice would be made by whichever sort happened to come first.
 *
 * So the surface is a PERSON-CENTRED READ. It names the person, then lists
 * EVERY manager grouped by the scope they hold authority at, then everyone who
 * reports to them. The count is stated in words above the list, so a manager
 * silently missing from the list contradicts a sentence on the same screen
 * rather than merely being absent.
 *
 * ---------------------------------------------------------------------------
 * DELEGATED AUTHORITY IS SHOWN WITH ITS EXPIRY, ALWAYS
 * ---------------------------------------------------------------------------
 * `GET /orgchart/users/:id/effective-authority` re-resolves coverage from
 * `starts_at <= now() < ends_at` on every call. Nothing is materialised, so
 * nothing has to be revoked and there is no cleanup job that can fail to run.
 * A screen that showed "covering for Dana" without "until Thursday 17:00" would
 * turn that guarantee back into an administrative worry, so the end time is
 * rendered beside every delegation and the sentence says the window closes by
 * itself.
 *
 * The read is NOT cached across that boundary: `staleTime: 0` here overrides
 * the app default, because this answer is a statement about an instant and the
 * instant is in the payload.
 */

/**
 * THE ORG CHART SURFACE — four questions about the same authority structure.
 *
 * ---------------------------------------------------------------------------
 * WHY TABS AND NOT FOUR ROUTES
 * ---------------------------------------------------------------------------
 * These are four views of one object: the organization's own authorization
 * structure. Reading a person's line, moving authority temporarily, editing the
 * structure, and auditing every change to it are the same administrator's work
 * in one sitting, and splitting them across four rail entries would put three
 * of them behind a navigation the reader has no reason to expect.
 *
 * It is also the shape this lane could actually ship: `App.tsx` is
 * integrator-owned, so a new route is a request rather than an edit. The
 * request is in this lane's report; the surface does not depend on it.
 *
 * ---------------------------------------------------------------------------
 * EVERY TAB GATES ITSELF
 * ---------------------------------------------------------------------------
 * The route gate establishes `orgchart:read` and nothing more. Delegation
 * charges `orgchart:delegate`, editing charges `orgchart:manage`, and the two
 * are held independently — so each tab asks its own question through
 * `authorityGate`, which distinguishes "not held" from "not yet known" and from
 * "could not be read". The tabs themselves are NOT hidden by permission: a
 * reader who cannot delegate should be able to see that delegation exists and
 * be told what it needs, rather than meet a surface with a hole in it.
 */
const TABS = [
  { key: 'line', label: 'Reporting line' },
  { key: 'coverage', label: 'Delegation & coverage' },
  { key: 'structure', label: 'Relationships & teams' },
  { key: 'history', label: 'Authority history' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

export function OrgChart() {
  const [tab, setTab] = useState<TabKey>('line');
  const tabKeys = useTabListKeys<HTMLDivElement>();

  return (
    <div className="rn-col" style={{ maxWidth: 900 }}>
      <header className="rn-col">
        <h1>Org chart</h1>
        <p className="rn-muted">
          This is your organization's own authorization structure: who is accountable for whom, and
          how that authority moves when somebody is away. Every control here changes what a person
          can do, so each one says what it grants, to whom, and until when before it is pressed.
        </p>
      </header>

      <div className="org-tabs" role="tablist" aria-label="Org chart sections" {...tabKeys}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`orgchart-tab-${t.key}`}
            aria-selected={tab === t.key}
            aria-controls={`orgchart-panel-${t.key}`}
            className={`org-tab${tab === t.key ? ' is-active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`orgchart-panel-${tab}`}
        aria-labelledby={`orgchart-tab-${tab}`}
        className="rn-col"
      >
        {tab === 'line' ? <ReportingLineTab /> : null}
        {tab === 'coverage' ? <DelegationCoverage /> : null}
        {tab === 'structure' ? <ChartEditing /> : null}
        {tab === 'history' ? <AuthorityHistory /> : null}
      </div>
    </div>
  );
}

function ReportingLineTab() {
  const permissions = usePermissions();
  /**
   * `mayListWorkers`, not the flattened `has()`.
   *
   * `GET /directory/workers` charges both keys at ORGANIZATION scope and 403s
   * rather than returning a shorter roster (`permissions.ts:225`). `has()` is
   * the flattened, reveal-a-screen check, so it is TRUE for a grant confined to
   * one program — which meant the explanatory sentence below was suppressed for
   * exactly the reader who needed it, the query fired anyway, the 403 was never
   * looked at, and the picker rendered as an apparently-usable dropdown holding
   * "Choose someone…" and nothing else. The reader concludes there is nobody in
   * their organization.
   */
  const canReadDirectory = mayListWorkers(permissions);
  const qc = useQueryClient();
  const [subjectUserId, setSubjectUserId] = useState<string>('');
  const [showHistory, setShowHistory] = useState(false);
  const [endError, setEndError] = useState<string | null>(null);

  /**
   * Ending an edge is `orgchart:manage`, which is NOT the key that got the
   * reader onto this screen (`orgchart:read`). Four states, and only a resolved
   * set may say "you may not".
   */
  const manageGate = authorityGate(permissions, 'orgchart:manage');

  const workers = useQuery({
    queryKey: ['directory', 'workers', 'for-orgchart'],
    queryFn: () => directory.workers(),
    enabled: canReadDirectory,
  });

  const people = personDirectory(
    { data: workers.data, isLoading: workers.isLoading, isError: workers.isError },
    canReadDirectory,
  );

  const line = useQuery({
    queryKey: ['orgchart', 'reporting-line', subjectUserId],
    queryFn: () => orgchart.reportingLine(subjectUserId),
    enabled: subjectUserId !== '',
  });

  const authority = useQuery({
    queryKey: ['orgchart', 'effective-authority', subjectUserId],
    queryFn: () => orgchart.effectiveAuthority(subjectUserId),
    enabled: subjectUserId !== '',
    // See the docblock: a live-resolved answer must not be served from a cache
    // that outlives the window it describes.
    staleTime: 0,
    gcTime: 0,
  });

  /**
   * EVERY relationship ever recorded for this person, not only the live ones.
   *
   * `reportingLine` answers as-of a date; this is the ledger behind it. It is
   * behind a disclosure because it is the rarer question, and it is fetched
   * only when opened so that reading the chart does not pay for it.
   */
  const history = useQuery({
    queryKey: ['orgchart', 'relationship-history', subjectUserId],
    queryFn: () => orgchart.relationshipHistory(subjectUserId),
    enabled: showHistory && subjectUserId !== '',
  });

  const endRelationship = useMutation({
    mutationFn: (input: { id: string; effectiveTo: string }) =>
      orgchart.endRelationship(input.id, input.effectiveTo),
    onSuccess: () => {
      setEndError(null);
      void qc.invalidateQueries({ queryKey: ['orgchart'] });
    },
    onError: (err: unknown) =>
      setEndError(writeRefusalMessage(err, 'We couldn’t end that relationship.')),
  });

  const candidates = (workers.data ?? []).filter((w) => w.userId);

  /**
   * WHY the picker has nothing in it — the four answers told apart.
   *
   * A refusal claimed only from a RESOLVED permission set: `usePermissions`
   * reads empty while it loads and after it fails, and "you may not see the
   * roster" said off a set that never arrived accuses somebody of lacking
   * access they hold. Until then this is `loading`, which is what it is.
   */
  const rosterWithheld = !permissions.isLoading && !permissions.isError && !canReadDirectory;
  const personPicker = pickerState(
    rosterWithheld
      ? { kind: 'refused' }
      : listPhase(
          permissions.isLoading || permissions.isError
            ? { isLoading: true, isError: false, error: null, data: undefined }
            : workers,
          candidates.length,
        ),
    'people',
    'Choose someone…',
  );

  return (
    <div className="rn-col">
      <header className="rn-col">
        <h2>Reporting line</h2>
        <p className="rn-muted">
          Who is accountable for whom, and who currently holds authority that was lent to them. A
          person can have more than one manager here — an organizational manager and a program
          supervisor are different questions with different answers, and both are shown.
        </p>
      </header>

      <label>
        Person
        <select
          value={subjectUserId}
          disabled={personPicker.disabled}
          onChange={(e) => setSubjectUserId(e.target.value)}
        >
          <option value="">{personPicker.placeholder}</option>
          {candidates.map((worker) => (
            <option key={worker.userId!} value={worker.userId!}>
              {worker.displayName}
            </option>
          ))}
        </select>
      </label>

      {/* An inert control with a sentence beside it, rather than an operable
          one offering nothing. `note` covers withheld and failed; the extra
          clause is what this screen alone can add — the chart is readable even
          when the names are not. */}
      {personPicker.note ? (
        <p className="rn-muted" role="status">
          {personPicker.note}
          {rosterWithheld ? ' The chart itself is readable; the names are not.' : ''}
        </p>
      ) : null}

      {subjectUserId === '' ? null : line.isLoading ? (
        <SkeletonSet surface="drawer" rows={6} />
      ) : line.isError && !line.data ? (
        <LoadFailure
          error={line.error}
          message="We couldn’t load this person’s reporting line just now. This is not a statement that they have no manager."
          onRetry={() => void line.refetch()}
        />
      ) : line.data ? (
        <ReportingLine
          line={line.data}
          people={people}
          authority={authority.data}
          authorityFailed={authority.isError}
          manageGate={manageGate}
          onEnd={(id, effectiveTo) => endRelationship.mutate({ id, effectiveTo })}
          ending={endRelationship.isPending}
          endError={endError}
          history={history}
          showHistory={showHistory}
          onToggleHistory={() => setShowHistory((open) => !open)}
        />
      ) : null}
    </div>
  );
}

function ReportingLine({
  line,
  people,
  authority,
  authorityFailed,
  manageGate,
  onEnd,
  ending,
  endError,
  history,
  showHistory,
  onToggleHistory,
}: {
  line: orgchart.ReportingLineView;
  people: PersonDirectory;
  authority: orgchart.EffectiveAuthorityView | undefined;
  authorityFailed: boolean;
  manageGate: ReturnType<typeof authorityGate>;
  onEnd: (relationshipId: string, effectiveTo: string) => void;
  ending: boolean;
  endError: string | null;
  history: {
    data: orgchart.RelationshipView[] | undefined;
    isLoading: boolean;
    isError: boolean;
  };
  showHistory: boolean;
  onToggleHistory: () => void;
}) {
  const subject = people.person(line.userId);
  const groups = orgchart.managersByScope(line);
  const now = new Date();
  const mayManage = gateOpen(manageGate);
  const manageNote = gateSentence(manageGate, 'end a reporting relationship');
  // The end date offered is TODAY, in the viewer's own calendar spelling
  // (`effective_to` is a `date`, not an instant). Rendering an instant here
  // would be a 400 and, worse, would suggest a precision the column has not —
  // and the UTC date (`toISOString().slice(0,10)`) wrote tomorrow's date into
  // the org calendar every evening.
  const today = localDayString(now);

  return (
    <article className="rn-col">
      <header className="rn-row">
        <PersonChip person={subject} size={40} detail={`as of ${formatDate(line.asOf)}`} />
      </header>

      <section className="rn-col" aria-label="Managers">
        <h2>Who manages {subject.displayName}</h2>
        {/*
          The count, in words, above the list. A manager missing from the list
          now contradicts a sentence on the same screen.
        */}
        <p data-manager-count={line.managers.length}>
          {managerCountSentence(subject.displayName, line.managers.length)}
        </p>

        {groups.map((group) => (
          <div key={group.scopeType} className="rn-col" data-scope-group={group.scopeType}>
            <h3>{group.label}</h3>
            <ul className="rn-list">
              {group.managers.map((edge) => {
                const view = managerLine(edge);
                return (
                  <li key={view.relationshipId} data-relationship-kind={edge.relationshipKind}>
                    <PersonChip person={people.person(view.managerUserId)} detail={view.capacity} />
                    <span className="rn-muted">
                      {' '}
                      · since {formatDate(view.since)}
                      {view.until ? ` · until ${formatDate(view.until)}` : ''}
                    </span>
                    {view.authorityBearing ? null : (
                      // A customer-defined type that does not route work. Saying
                      // so out loud is the difference between an org chart and a
                      // list of people who know each other.
                      <Pill variant="plain">Informational — routes no work</Pill>
                    )}
                    {edge.ended ? (
                      <span className="rn-muted"> · already ended</span>
                    ) : (
                      <Button
                        variant="destructive"
                        small
                        disabled={!mayManage || ending}
                        onClick={() => onEnd(edge.id, today)}
                      >
                        End this relationship today
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </section>

      {manageNote ? (
        <p className="rn-muted" role="status" data-manage-gate={manageGate.kind}>
          {manageNote}
        </p>
      ) : null}
      {endError ? (
        <p role="alert" data-end-relationship-error="true">
          {endError}
        </p>
      ) : null}

      <section className="rn-col" aria-label="Reports">
        <h2>Who reports to {subject.displayName}</h2>
        <p>{reportCountSentence(subject.displayName, line.reports.length)}</p>
        {line.reports.length === 0 ? null : (
          <ul className="rn-list">
            {line.reports.map((edge) => {
              const view = managerLine(edge);
              return (
                <li key={view.relationshipId}>
                  <PersonChip
                    person={people.person(edge.subordinateUserId)}
                    detail={`${view.capacity} · ${view.scope}`}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="rn-col" aria-label="Relationship history">
        <h2>Every relationship ever recorded</h2>
        <ConsequenceNote text="The lists above answer as of one date. This is the ledger behind them, including edges that have already ended — which is the only place a reader can see that somebody USED to route work here." />
        <Button variant="quiet" small onClick={onToggleHistory}>
          {showHistory ? 'Hide the history' : 'Show the history'}
        </Button>
        {!showHistory ? null : history.isLoading ? (
          <SkeletonSet surface="drawer" rows={3} />
        ) : history.isError ? (
          <p className="rn-muted" role="status">
            We couldn’t load the history. Do not read this as “nothing was ever recorded” — it is a
            failure to ask.
          </p>
        ) : (history.data ?? []).length === 0 ? (
          <SuccessEmpty
            heading="Nothing recorded"
            message={`The server was asked and returned no relationships for ${subject.displayName}, live or ended.`}
          />
        ) : (
          <ul className="rn-list">
            {(history.data ?? []).map((edge) => (
              <li key={edge.id} data-history-edge={edge.id} data-history-ended={String(edge.ended)}>
                <PersonChip
                  person={people.person(edge.managerUserId)}
                  detail={managerLine(edge).capacity}
                />
                <span className="rn-muted">
                  {' '}
                  · {formatDate(edge.effectiveFrom)} →{' '}
                  {edge.effectiveTo ? formatDate(edge.effectiveTo) : 'open'}
                  {edge.ended ? ' · ended' : ' · live'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rn-col" aria-label="Delegated authority">
        <h2>Authority held right now</h2>
        {authorityFailed ? (
          <p className="rn-muted">
            We couldn’t check what {subject.displayName} currently holds. This is not a statement
            that they hold nothing.
          </p>
        ) : authority === undefined ? (
          <SkeletonSet surface="drawer" rows={2} />
        ) : authority.delegated.length === 0 ? (
          <SuccessEmpty
            heading="Nothing delegated"
            message={`${subject.displayName} is not currently covering for anyone. Coverage is resolved at the moment it is asked for, so this answer is about now.`}
          />
        ) : (
          <ul className="rn-list">
            {authority.delegated.map((delegation) => (
              <li key={delegation.coverageId} data-coverage-ends={delegation.endsAt}>
                <PersonChip person={people.person(delegation.coveredUserId)} />
                <p>
                  {coverageWindowSentence(delegation, people.name(delegation.coveredUserId), now)}
                </p>
                <p className="rn-muted">{borrowedSummary(delegation)}</p>
              </li>
            ))}
          </ul>
        )}
        {authority ? (
          <p className="rn-muted">
            Resolved at {new Date(authority.at).toLocaleTimeString()}. Delegated authority is never
            written down — it is recomputed on every request, so it disappears at the end of its
            window without anyone revoking it.
          </p>
        ) : null}
      </section>
    </article>
  );
}
